import Alpine from 'alpinejs';
import { gsap } from 'gsap';
import RytmWebflow from 'rytm-webflow';

/**
 * Location carousel — Alpine data component
 *
 * Two horizontally-scrolled tracks that step in lockstep. Wrap the widget in
 * an element with `x-data="locationCarousel()"`. Mark the two scroll
 * containers with `x-ref="scrollerImage"` and `x-ref="scrollerDescr"` — each
 * one holds its own slides (same count, same order). Prev/next buttons call
 * `prev()` / `next()`; button enable state comes from `canPrev` / `canNext`.
 *
 * Navigation scrolls each track to its own target offset so the browser runs
 * two independent smooth-scroll animations in parallel. A short nav-lock
 * suppresses the user-drag sync handler for the animation window, otherwise
 * one track's scroll events would cancel the other's smooth scroll mid-flight.
 *
 * WebflowView scroll reveals inside off-screen slides are skipped by
 * rytm-webflow's viewport check at scene-add time, so elements inside slides
 * 2+ can stay at opacity 0 permanently. On navigation we manually fire their
 * show tween and mark them as fired so ScrollMagic won't re-trigger.
 */
const NAV_LOCK_MS = 700;
const SNAP_RESTORE_MS = 150;
const FIRED_CLASS = 'webflow-scroll-fired';

const revealWebscrollElementsIn = (slides: HTMLElement[]) => {
  for (const slide of slides) {
    const els = slide.querySelectorAll<HTMLElement>('[data-webscroll-show]');
    els.forEach((el) => {
      if (el.classList.contains(FIRED_CLASS)) return;
      const attr = el.dataset.webscrollShow;
      if (!attr) return;
      const propsShow = RytmWebflow.getWebflowAnimationProps(attr);
      if (!propsShow || !propsShow.tween) return;
      gsap.killTweensOf(el);
      gsap.to(el, {
        duration: propsShow.time,
        ...propsShow.tween,
        onComplete: () => {
          el.classList.add(FIRED_CLASS);
        },
      });
    });
  }
};

export const initLocationCarousel = () => {
  Alpine.data('locationCarousel', () => {
    let scrollers: HTMLElement[] = [];
    let primary: HTMLElement | null = null;
    let handlers: Array<[HTMLElement, () => void]> = [];
    let syncing = false;
    let navLockUntil = 0;
    let refreshTimer: number | null = null;
    let snapRestoreTimer: number | null = null;
    let activeIndex = -1;

    const restoreSnap = () => {
      for (const s of scrollers) s.style.scrollSnapType = '';
    };

    const cancelSnapRestore = () => {
      if (snapRestoreTimer !== null) {
        window.clearTimeout(snapRestoreTimer);
        snapRestoreTimer = null;
      }
    };

    const scheduleSnapRestore = () => {
      cancelSnapRestore();
      snapRestoreTimer = window.setTimeout(() => {
        restoreSnap();
        snapRestoreTimer = null;
      }, SNAP_RESTORE_MS);
    };

    const syncFrom = (source: HTMLElement) => {
      if (syncing || Date.now() < navLockUntil) return;
      syncing = true;
      const max = source.scrollWidth - source.clientWidth;
      const ratio = max > 0 ? source.scrollLeft / max : 0;
      for (const s of scrollers) {
        if (s === source) continue;
        s.style.scrollSnapType = 'none';
        const localMax = s.scrollWidth - s.clientWidth;
        s.scrollLeft = ratio * localMax;
      }
      requestAnimationFrame(() => { syncing = false; });
      scheduleSnapRestore();
      revealSlide(currentIndex(source));
    };

    const currentIndex = (s: HTMLElement): number => {
      const slides = Array.from(s.children) as HTMLElement[];
      if (slides.length === 0) return 0;
      const first = slides[0].getBoundingClientRect().width;
      if (first === 0) return 0;
      return Math.round(s.scrollLeft / first);
    };

    const revealSlide = (idx: number) => {
      if (idx === activeIndex) return;
      activeIndex = idx;
      const activeSlides: HTMLElement[] = [];
      for (const s of scrollers) {
        const slide = s.children[idx] as HTMLElement | undefined;
        if (slide) activeSlides.push(slide);
      }
      revealWebscrollElementsIn(activeSlides);
    };

    const goTo = (direction: 1 | -1) => {
      if (!primary) return;
      const slides = Array.from(primary.children) as HTMLElement[];
      const idx = Math.max(0, Math.min(slides.length - 1, currentIndex(primary) + direction));
      navLockUntil = Date.now() + NAV_LOCK_MS;
      cancelSnapRestore();
      restoreSnap();
      for (const s of scrollers) {
        const slide = s.children[idx] as HTMLElement | undefined;
        if (!slide) continue;
        s.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }
      revealSlide(idx);
      if (refreshTimer !== null) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        RytmWebflow.scrollController.refresh();
        refreshTimer = null;
      }, NAV_LOCK_MS);
    };

    return {
      canPrev: false as boolean,
      canNext: true as boolean,

      init() {
        const a = (this.$refs['scrollerImage'] as HTMLElement) ?? null;
        const b = (this.$refs['scrollerDescr'] as HTMLElement) ?? null;
        scrollers = [a, b].filter((el): el is HTMLElement => el !== null);
        primary = scrollers[0] ?? null;
        if (!primary) return;

        for (const s of scrollers) {
          const handler = () => {
            syncFrom(s);
            if (s === primary) this.updateEdges();
          };
          s.addEventListener('scroll', handler, { passive: true });
          handlers.push([s, handler]);
        }

        this.$nextTick(() => this.updateEdges());
      },

      destroy() {
        for (const [s, h] of handlers) s.removeEventListener('scroll', h);
        cancelSnapRestore();
        restoreSnap();
        handlers = [];
        scrollers = [];
        primary = null;
        if (refreshTimer !== null) {
          window.clearTimeout(refreshTimer);
          refreshTimer = null;
        }
      },

      updateEdges() {
        if (!primary) return;
        const { scrollLeft, scrollWidth, clientWidth } = primary;
        this.canPrev = scrollLeft > 1;
        this.canNext = scrollLeft < scrollWidth - clientWidth - 1;
      },

      next() { goTo(1); },
      prev() { goTo(-1); },
    };
  });
};
